import { ChangeDetectionStrategy, Component, Input, OnDestroy, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { fiItemsFlatten } from '@fi-shared/utils/fi-items-flatten';
import {
    IChecklist2ReportContainer,
    IChecklistV2ReportParameter,
    IOpenChecklistReportWidgetDetailsData,
} from './shared/fi-checklist2-report';
import { takeUntil } from 'rxjs/operators';
import { FiChecklist2ReportService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report.service';
import { FiChecklist2ReportHelperService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-helper.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { ChecklistV2ReportPages } from './enums/fi-checklist2-report-pages.enum';
import { Animations } from '@fi-shared/utils/animations';
import { FiChecklist2ReportData } from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report-data';
import { FiChecklist2ReportDialogsService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-dialogs.service';
import { FiChecklist2ReportStateService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-state.service';
import { FiChecklist2ReportType } from '@fi-shared/checklist/fi-checklist2-report/enums/fi-checklist2-report-type';
import { FiChecklist2ReportDataService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-data.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { ItemsListFilterDialogSnackbarService } from '@fi-app/shared/fi-items-list/fi-items-list-filter/services/items-list-filter-dialog-snackbar.service';

@Component({
    selector: 'fi-checklist2-report',
    templateUrl: './fi-checklist2-report.component.html',
    styleUrls: ['./fi-checklist2-report.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist2-report' },
    animations: [Animations.fade],
    standalone: false,
})
export class FiChecklist2ReportComponent implements OnInit, OnDestroy {
    @Input({ required: true }) auditMode: boolean;

    loading$ = new BehaviorSubject<boolean>(false);

    currentWidgetDetailsData: IOpenChecklistReportWidgetDetailsData;

    reports$ = new BehaviorSubject<IChecklistV2ReportParameter[]>(undefined);
    reportData: FiChecklist2ReportData = new FiChecklist2ReportData();

    currentChecklistReportPage = ChecklistV2ReportPages.Dashboard;

    private fiChecklist2ReportService = inject(FiChecklist2ReportService);
    private fiChecklist2ReportDialogsService = inject(FiChecklist2ReportDialogsService);
    private fiChecklist2ReportHelperService = inject(FiChecklist2ReportHelperService);
    private fiChecklist2ReportDataService = inject(FiChecklist2ReportDataService);
    private fiChecklist2ReportStateService = inject(FiChecklist2ReportStateService);
    private saveChangesWarningService = inject(SaveChangesWarningService);
    private itemsListFilterDialogSnackbarService = inject(ItemsListFilterDialogSnackbarService);

    ngOnInit(): void {
        this.fiChecklist2ReportDataService.reset();
        this.getChecklistReportData();
        this.subscribeOnAddParameter();
        this.subscribeOnCreateWidget();
        this.subscribeOnSaveReportWidget();
        this.subscribeOnAddReportWidgetDialogClosed();
        this.setIsAddNewWidgetButtonShown();

        this.fiChecklist2ReportHelperService.onReportParamUpdate$().subscribe((reportParam) => {
            this.fiChecklist2ReportDataService.setWidgetFilter(reportParam);
        });
    }

    private getChecklistReportData() {
        this.loading$.next(true);
        Promise.all([this.getReportContainer() /*, this.setRecentActivity()*/]).then(() => {
            this.loading$.next(false);

            let currentReportParam: IChecklistV2ReportParameter;
            if (this.fiChecklist2ReportStateService.shouldShowOpenChecklistsReport()) {
                currentReportParam = this.reports$.value.find(
                    (item) => item.ReportType === FiChecklist2ReportType.OpenChecklists,
                );
            } else if (this.fiChecklist2ReportStateService.shouldShowPastChecklistsReport()) {
                currentReportParam = this.reports$.value.find(
                    (item) => item.ReportType === FiChecklist2ReportType.PastChecklists,
                );
            } else if (this.fiChecklist2ReportStateService.shouldShowAttentionRequiredReport()) {
                currentReportParam = this.reports$.value.find(
                    (item) => item.ReportType === FiChecklist2ReportType.AttentionRequired,
                );
            }
            if (currentReportParam) {
                this.showWidgetDetails({
                    reportParam: currentReportParam,
                    isFilterExpanded: false,
                });
            }
        });
        // this.getReportContainer().then(() => {
        //     this.loading$.next(false);
        //
        //     let currentReportParam: IChecklistV2ReportParameter;
        //     if (this.fiChecklist2ReportStateService.shouldShowOpenChecklistsReport()) {
        //         currentReportParam = this.reports$.value.find(item => item.ReportType === FiChecklist2ReportType.OpenChecklists);
        //     }
        //     else if (this.fiChecklist2ReportStateService.shouldShowPastChecklistsReport()) {
        //         currentReportParam = this.reports$.value.find(item => item.ReportType === FiChecklist2ReportType.PastChecklists);
        //     }
        //     else if (this.fiChecklist2ReportStateService.shouldShowAttentionRequiredReport()) {
        //         currentReportParam = this.reports$.value.find(item => item.ReportType === FiChecklist2ReportType.AttentionRequired);
        //     }
        //     if (currentReportParam) {
        //         this.showWidgetDetails({
        //             reportParam: currentReportParam,
        //             isFilterExpanded: false
        //         });
        //     }
        // });
    }

    private async getReportContainer(): Promise<void> {
        const reportContainer: IChecklist2ReportContainer = await this.fiChecklist2ReportService.getReportContainer(
            this.auditMode,
        );
        reportContainer.ReportParameters = reportContainer.ReportParameters.filter((parameter) => {
            return this.fiChecklist2ReportHelperService.isReportTypeAvailable(parameter.ReportType, this.auditMode);
        });

        reportContainer.AvailableItems.Areas = fiItemsFlatten(reportContainer.AvailableItems.Areas);
        this.reportData.setReportContainerData(reportContainer, this.auditMode);

        this.reports$.next(reportContainer.ReportParameters);
    }

    subscribeOnAddParameter() {
        this.fiChecklist2ReportHelperService
            .onNewParameterAdded$()
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((newWidget: IChecklistV2ReportParameter) => {
                this.addNewWidget(newWidget);
            });
    }

    private addNewWidget(newWidget: IChecklistV2ReportParameter) {
        if (!newWidget) return;

        this.reports$.next([...this.reports$.value, newWidget]);
    }

    subscribeOnCreateWidget() {
        this.fiChecklist2ReportHelperService
            .onTriggerAddNewWidgetOpenDialog$()
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => {
                this.fiChecklist2ReportDialogsService.openAddChecklistReportWidgetDialog(this.reportData);
                this.saveChangesWarningService.closeDialogOnTransition = true;
            });
    }

    private subscribeOnSaveReportWidget(): void {
        this.fiChecklist2ReportDialogsService.saveReportWidget$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((reportParam: IChecklistV2ReportParameter) => {
                this.saveReportParam(reportParam);
            });
    }

    private subscribeOnAddReportWidgetDialogClosed(): void {
        this.fiChecklist2ReportDialogsService.addReportWidgetDialogClosed$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => {
                this.saveChangesWarningService.closeDialogOnTransition = false;
            });
    }

    private saveReportParam(reportParam: IChecklistV2ReportParameter) {
        const oldReportParameterId = reportParam.Id; // should be 0
        this.fiChecklist2ReportService
            .saveReportParameter(reportParam, this.auditMode)
            .then((reportParameterId: number) => {
                reportParam.Id = reportParameterId;
                // reportData should be moved from old parameter id to new one
                this.reportData.deleteReportData(oldReportParameterId);
                this.fiChecklist2ReportService
                    .getDataForReportParameter(reportParam, this.reportData.auditMode)
                    .then((reportData) => {
                        this.reportData.setReportData(reportData, reportParam.Id);
                        this.addNewWidget(reportParam);
                        this.itemsListFilterDialogSnackbarService.showWidgetAddedSnackbar();
                    });
            });
    }

    onReportsChange(newReports: IChecklistV2ReportParameter[]) {
        this.reports$.next(newReports);
    }

    showWidgetDetails(widgetDetailsData: IOpenChecklistReportWidgetDetailsData) {
        this.currentWidgetDetailsData = widgetDetailsData;
        this.currentChecklistReportPage = this.fiChecklist2ReportHelperService.getCurrentReportPageByReportType(
            widgetDetailsData.reportParam.ReportType,
        );
        this.fiChecklist2ReportStateService.onShowWidgetSummaryPage();
        this.setIsAddNewWidgetButtonShown();
    }

    onCurrentChecklistReportPageChanged() {
        this.setIsAddNewWidgetButtonShown();
    }

    isDashboardDisplayed(): boolean {
        return this.currentChecklistReportPage === ChecklistV2ReportPages.Dashboard;
    }

    private setIsAddNewWidgetButtonShown() {
        const isAddNewWidgetButtonShown = this.isDashboardDisplayed();
        this.fiChecklist2ReportHelperService.setIsAddNewWidgetButtonShown(isAddNewWidgetButtonShown);
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
